import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassInput } from '@/components/ui/GlassInput'
import { GlassButton } from '@/components/ui/GlassButton'
import { signInWithPasskey, signInWithEmail } from '@/lib/auth-client'
import { Fingerprint, Mail, ChevronDown } from 'lucide-react'
import config from '@config'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/auth/login')({
  component: LoginPage,
})

function LoginPage() {
  const t = useT()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showEmailForm, setShowEmailForm] = useState(false)

  async function handlePasskeyLogin() {
    setLoading(true)
    setError('')
    try {
      const res = await signInWithPasskey()
      if (res?.error) {
        if (res.error.code === 'INVALID_PASSKEY_OPTIONS') {
          setShowEmailForm(true)
          throw new Error(t('auth.login.passkeyInvalidOptions'))
        }
        throw new Error(res.error.message ?? t('auth.login.passkeyFailed'))
      }
      await navigate({ to: '/dashboard' })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.login.passkeyFailed'))
    } finally {
      setLoading(false)
    }
  }

  async function handleEmailLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const res = await signInWithEmail(email, password)
      if (res?.error) throw new Error(res.error.message ?? t('auth.login.signInFailed'))
      await navigate({ to: '/dashboard' })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.login.invalidCredentials'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--oc-bg)] p-4">
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(124,58,237,0.08) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />
      <GlassCard glow className="w-full max-w-sm space-y-6 relative z-10">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-bold text-white glow-text">{config.app.name}</h1>
          <p className="text-sm text-white/50">{t('auth.login.title')}</p>
        </div>

        {/* Primary CTA: Passkey */}
        {config.features.passkeys && (
          <GlassButton
            size="lg"
            variant="primary"
            className="w-full"
            onClick={handlePasskeyLogin}
            loading={loading && !showEmailForm}
          >
            <Fingerprint size={18} />
            {t('auth.login.passkey')}
          </GlassButton>
        )}

        {error && <p className="text-red-400 text-sm text-center">{error}</p>}

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-white/30">{t('common.or')}</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Collapsible email + password fallback */}
        <div>
          <button
            type="button"
            onClick={() => setShowEmailForm(!showEmailForm)}
            className="flex items-center gap-2 text-sm text-white/45 hover:text-white/70 transition-colors w-full justify-center"
          >
            <Mail size={14} />
            {t('auth.login.useEmail')}
            <ChevronDown
              size={14}
              className={`transition-transform ${showEmailForm ? 'rotate-180' : ''}`}
            />
          </button>

          {showEmailForm && (
            <form onSubmit={handleEmailLogin} className="mt-4 space-y-3">
              <GlassInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.login.emailPlaceholder')}
                required
                autoComplete="email"
                autoFocus
              />
              <GlassInput
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.login.passwordPlaceholder')}
                required
                autoComplete="current-password"
              />
              <GlassButton
                type="submit"
                variant="secondary"
                size="md"
                className="w-full"
                loading={loading && showEmailForm}
              >
                {t('auth.login.signIn')}
              </GlassButton>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-white/40">
          {t('auth.login.noAccount')}{' '}
          <Link to="/auth/register" className="text-[var(--oc-accent-light)] hover:underline">
            {t('auth.login.register')}
          </Link>
        </p>
      </GlassCard>
    </div>
  )
}
